package com.iptvplayer.shared.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.iptvplayer.shared.domain.repository.AuthRepository
import com.iptvplayer.shared.presentation.state.AuthUiState
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AuthViewModel(
    private val authRepository: AuthRepository
) : ViewModel() {

    private val _state = MutableStateFlow(AuthUiState())
    val state: StateFlow<AuthUiState> = _state.asStateFlow()

    init {
        checkLoginStatus()
    }

    private fun checkLoginStatus() {
        viewModelScope.launch {
            val user = authRepository.getCurrentUser()
            val token = authRepository.getStoredToken()
            _state.update {
                it.copy(
                    isLoggedIn = user != null && token != null,
                    isInitialized = true,
                    user = user
                )
            }
        }
    }

    fun login(username: String, password: String) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            when (val result = authRepository.login(username, password)) {
                is Result.Success -> {
                    authRepository.saveToken(result.data.tokens)
                    authRepository.saveUser(result.data.user)
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isLoggedIn = true,
                            user = result.data.user
                        )
                    }
                }
                is Result.Error -> {
                    _state.update { it.copy(isLoading = false, error = result.message) }
                }
                is Result.Loading -> {}
            }
        }
    }

    fun register(username: String, email: String, password: String) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            when (val result = authRepository.register(username, email, password)) {
                is Result.Success -> {
                    authRepository.saveToken(result.data.tokens)
                    authRepository.saveUser(result.data.user)
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isLoggedIn = true,
                            user = result.data.user
                        )
                    }
                }
                is Result.Error -> {
                    _state.update { it.copy(isLoading = false, error = result.message) }
                }
                is Result.Loading -> {}
            }
        }
    }

    fun logout() {
        viewModelScope.launch {
            authRepository.logout()
            authRepository.clearSession()
            // Keep isInitialized so splash gates don't re-engage after logout.
            _state.update { AuthUiState(isInitialized = true) }
        }
    }

    /**
     * Submits an account-deletion request. The account is not removed
     * immediately — an admin reviews and approves the deletion. The user
     * stays signed in; [onRequested] runs only on success.
     */
    fun requestAccountDeletion(onRequested: () -> Unit = {}) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            when (val result = authRepository.requestAccountDeletion()) {
                is Result.Success -> {
                    _state.update { it.copy(isLoading = false) }
                    onRequested()
                }
                is Result.Error -> {
                    _state.update { it.copy(isLoading = false, error = result.message) }
                }
                is Result.Loading -> {}
            }
        }
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }
}
